import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, COMMON } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

const isMGBW = isThemeActive(THEME_IDS.MGBW);

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            background-color: ${getColor("primary")};
            margin-top: 50px;
            ${isMGBW && css`
                background-color: #fff;
                border-top: 1px solid ${COLORS.border.main};
                margin-top: 0;
            `}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 34px 21px 0px 20px;
                        margin-top: 0px;
                    `,
                    css`
                        padding: 34px 21px 0px 20px;
                        margin-top: 0px;
                    `,
                    css`
                        padding: 18px 21px 0px 20px;
                        margin-top: 0px;
                    `,
                    null,
                    null,
                ])}
            ${isMGBW && css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 16px 16px 20px 16px;
                            margin-top: 0;
                            div[class^="GridItemStyle-sc"]:last-child:has(.social-links) {
                                margin-top: 0 !important;
                            }
                        `,
                        css`
                            padding: 16px 16px 20px 16px;
                            margin-top: 0;
                            div[class^="GridItemStyle-sc"]:last-child:has(.social-links) {
                                margin-top: 0 !important;
                            }
                        `,
                        css`
                            padding: 16px 16px 20px 16px;
                            margin-top: 0;
                            div[class^="GridItemStyle-sc"]:last-child:has(.social-links) {
                                margin-top: 0 !important;
                            }
                        `,
                        css`
                            padding: 50px 80px 0 80px;
                            margin-top: 0;
                        `,
                        css`
                            padding: 50px 80px 0 80px;
                            margin-top: 0;
                        `,
                    ])}
            `}
        `,
    },
    logoGridItem: {
        css: css`
            width: 100%;
            max-width: ${COMMON.width.content}px;
            margin: 0 auto;
            ${isMGBW && css`
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                max-width: 100%;
                gap: 50px 0;
            `}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        width: calc(100% - 40px);
                    `,
                    isMGBW ? null : css`
                        width: calc(100% - 80px);
                    `,
                    isMGBW ? null : css`
                        width: calc(100% - 80px);
                    `,
                ])}
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.widgetGridContainer}>
            <StyledWrapper {...styles.logoGridItem}>
                <Zone zoneName="footerContainer" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
